import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Music2, RefreshCcw } from "lucide-react";
import { playableUrl } from "@/lib/creatorApi";
import { VOICE_PREVIEW_BUSY, getVoicePreview, makeVoicePreview } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const STEP = {
  starting: "Getting ready…",
  band: "Making the gospel band — first time only, about a minute…",
  singing: "Your new voice is singing the line…",
  mixing: "Mixing it over the band…",
};

/** "Hallelujah, Your mercy carried me" sung by this AI voice over a full gospel band — made automatically, re-made on demand. */
export default function VoicePreview({ profileId, initial }) {
  const [preview, setPreview] = useState(initial || null);
  const busy = VOICE_PREVIEW_BUSY.includes(preview?.status);

  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(() => getVoicePreview(profileId).then(setPreview).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [busy, profileId]);

  const again = async () => {
    try { setPreview(await makeVoicePreview(profileId)); } catch (e) { toast.error("Couldn't start the preview", { description: safeApiError(e) }); }
  };

  return (
    <div data-testid={`voice-preview-${profileId}`} className="mt-3 rounded-2xl border border-violet-300/20 bg-black/30 p-3">
      <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-violet-200/90"><Music2 className="h-3 w-3" /> Voice preview · “Hallelujah, Your mercy carried me”</p>
      {busy ? (
        <p data-testid={`voice-preview-status-${profileId}`} className="mt-2 flex items-center gap-2 text-[12px] text-white/70"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {STEP[preview.status]}</p>
      ) : null}
      {preview?.audio_url && !busy ? <audio data-testid={`voice-preview-player-${profileId}`} controls preload="metadata" src={playableUrl(preview.audio_url)} className="mt-2 w-full" /> : null}
      {preview?.status === "error" ? <p data-testid={`voice-preview-error-${profileId}`} className="mt-2 text-[12px] text-rose-200">{preview.error || "The preview didn't finish."}</p> : null}
      {!busy ? (
        <button type="button" data-testid={`voice-preview-make-${profileId}`} onClick={again} className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 transition-colors hover:text-white">
          <RefreshCcw className="h-3 w-3" /> {preview?.audio_url ? "Sing it again" : "Make the preview"}
        </button>
      ) : null}
    </div>
  );
}
